/**
 * The analytics' per-browser facts, kept in local storage under `highmarch.analytics.*` (not in the save, so a save
 * reset or import does not make a new player of the same person): an anonymous random id, the day it was made and how
 * many sessions have started. Storage refused (private mode): a fresh id each session, counted as session 1.
 */
export interface InstallStats {
  readonly id: string;
  /** Whole days since this browser's first session. */
  readonly days: number;
  /** Sessions started here, this one included. */
  readonly sessions: number;
}

export interface KeyValue { getItem(key: string): string | null; setItem(key: string, value: string): void }

const IdKey = "highmarch.analytics.id";
const SinceKey = "highmarch.analytics.since";
const SessionsKey = "highmarch.analytics.sessions";
const DayMs = 86_400_000;

/** Reads (making on the first session) and counts this session. */
export function startSession(store: KeyValue | null, nowMs: number, newId: () => string): InstallStats {
  try {
    if (!store) throw new Error("no storage");
    let id = store.getItem(IdKey);
    let since = Number(store.getItem(SinceKey));
    if (!id || !Number.isFinite(since) || since <= 0) {
      id = newId();
      since = nowMs;
      store.setItem(IdKey, id);
      store.setItem(SinceKey, String(since));
    }
    const prior = Number(store.getItem(SessionsKey));
    const sessions = (Number.isFinite(prior) && prior > 0 ? Math.floor(prior) : 0) + 1;
    store.setItem(SessionsKey, String(sessions));
    return { id, days: Math.max(0, Math.floor((nowMs - since) / DayMs)), sessions };
  } catch {
    return { id: newId(), days: 0, sessions: 1 };
  }
}
